import {
  saveTelnyxProvider,
  getTelnyxProvider,
  testTelnyxConnection,
  deleteTelnyxProvider,
  searchPhoneNumbers,
  provisionPhoneNumber,
  getPhoneNumbers,
  releasePhoneNumber,
  sendFax,
  getFaxHistory,
} from "../../api/telnyx";

const state = {
  provider: null,
  loading: false,
  testResult: null,
  testLoading: false,
  // Phone numbers
  phoneNumbers: [],
  numbersLoading: false,
  searchResults: [],
  searchLoading: false,
  // Fax
  faxHistory: [],
  faxLoading: false,
  faxSending: false,
};

const getters = {
  provider: (state) => state.provider,
  isConnected: (state) =>
    state.provider && state.provider.verified && state.provider.active,
  loading: (state) => state.loading,
  testResult: (state) => state.testResult,
  testLoading: (state) => state.testLoading,
  phoneNumbers: (state) => state.phoneNumbers,
  numbersLoading: (state) => state.numbersLoading,
  searchResults: (state) => state.searchResults,
  searchLoading: (state) => state.searchLoading,
  faxHistory: (state) => state.faxHistory,
  faxLoading: (state) => state.faxLoading,
  faxSending: (state) => state.faxSending,
};

const mutations = {
  SET_PROVIDER(state, provider) { state.provider = provider; },
  SET_LOADING(state, v) { state.loading = v; },
  SET_TEST_RESULT(state, v) { state.testResult = v; },
  SET_TEST_LOADING(state, v) { state.testLoading = v; },
  CLEAR_PROVIDER(state) { state.provider = null; state.testResult = null; },
  SET_PHONE_NUMBERS(state, v) { state.phoneNumbers = v; },
  SET_NUMBERS_LOADING(state, v) { state.numbersLoading = v; },
  SET_SEARCH_RESULTS(state, v) { state.searchResults = v; },
  SET_SEARCH_LOADING(state, v) { state.searchLoading = v; },
  SET_FAX_HISTORY(state, v) { state.faxHistory = v; },
  SET_FAX_LOADING(state, v) { state.faxLoading = v; },
  SET_FAX_SENDING(state, v) { state.faxSending = v; },
};

const actions = {
  // ─── Provider ──────────────────────────────────────────────

  async loadProvider({ commit }) {
    commit("SET_LOADING", true);
    try {
      const response = await getTelnyxProvider();
      if (response.status === 204) {
        commit("SET_PROVIDER", null);
      } else {
        commit("SET_PROVIDER", response.data);
      }
    } catch (error) {
      commit("SET_PROVIDER", null);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async saveProvider({ commit }, data) {
    commit("SET_LOADING", true);
    try {
      const response = await saveTelnyxProvider(data);
      commit("SET_PROVIDER", response.data);
      commit("SET_TEST_RESULT", null);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async testConnection({ commit }) {
    commit("SET_TEST_LOADING", true);
    commit("SET_TEST_RESULT", null);
    try {
      const response = await testTelnyxConnection();
      commit("SET_TEST_RESULT", response.data);
      if (response.data.success) {
        const providerResponse = await getTelnyxProvider();
        if (providerResponse.status !== 204) {
          commit("SET_PROVIDER", providerResponse.data);
        }
      }
      return response.data;
    } catch (error) {
      const result = {
        success: false,
        message: error.response?.data?.message || "Connection test failed",
      };
      commit("SET_TEST_RESULT", result);
      return result;
    } finally {
      commit("SET_TEST_LOADING", false);
    }
  },

  async removeProvider({ commit }) {
    commit("SET_LOADING", true);
    try {
      await deleteTelnyxProvider();
      commit("CLEAR_PROVIDER");
    } catch (error) {
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // ─── Phone Numbers ────────────────────────────────────────

  async loadPhoneNumbers({ commit }) {
    commit("SET_NUMBERS_LOADING", true);
    try {
      const response = await getPhoneNumbers();
      commit("SET_PHONE_NUMBERS", response.data);
    } catch (error) {
      commit("SET_PHONE_NUMBERS", []);
    } finally {
      commit("SET_NUMBERS_LOADING", false);
    }
  },

  async searchNumbers({ commit }, params) {
    commit("SET_SEARCH_LOADING", true);
    try {
      const response = await searchPhoneNumbers(params);
      commit("SET_SEARCH_RESULTS", response.data);
      return response.data;
    } catch (error) {
      commit("SET_SEARCH_RESULTS", []);
      throw error;
    } finally {
      commit("SET_SEARCH_LOADING", false);
    }
  },

  async provisionNumber({ commit, dispatch }, data) {
    commit("SET_NUMBERS_LOADING", true);
    try {
      const response = await provisionPhoneNumber(data);
      await dispatch("loadPhoneNumbers");
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit("SET_NUMBERS_LOADING", false);
    }
  },

  async releaseNumber({ commit, dispatch }, numberId) {
    commit("SET_NUMBERS_LOADING", true);
    try {
      await releasePhoneNumber(numberId);
      await dispatch("loadPhoneNumbers");
    } catch (error) {
      throw error;
    } finally {
      commit("SET_NUMBERS_LOADING", false);
    }
  },

  // ─── Fax ──────────────────────────────────────────────────

  async loadFaxHistory({ commit }, params) {
    commit("SET_FAX_LOADING", true);
    try {
      const response = await getFaxHistory(params);
      commit("SET_FAX_HISTORY", response.data);
    } catch (error) {
      commit("SET_FAX_HISTORY", []);
    } finally {
      commit("SET_FAX_LOADING", false);
    }
  },

  async sendFax({ commit, dispatch }, data) {
    commit("SET_FAX_SENDING", true);
    try {
      const response = await sendFax(data);
      await dispatch("loadFaxHistory");
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit("SET_FAX_SENDING", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
